import { useTranslation } from 'next-i18next';
import { Button } from '@components/common';
import { CheckIcon, BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import {
  BookmarkIcon, FlagIcon, ShareIcon,
} from '@heroicons/react/24/outline';

import { ApiJobDetailOk, ApiJobUserDetailOk } from '@type/v1-api-types';
import { useJobTalentActions } from '@hooks/features/useJobTalentActions';
import {
  useContext, useEffect, useState,
} from 'react';
import { JobTrackingContext } from '@components/contexts/JobTrackingContext';
import { JobPlacement } from '@type/job-params';
import { clsx } from 'clsx';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useBjModal } from '@hooks/common/useBjModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { AiEvaluationFeedbackModal } from '@components/features/modals/ai-evaluation-feedback/AiEvaluationFeedbackModal';
import {
  InterviewResponseButton,
} from '@components/templates/job/after-apply/interview-selection/InterviewResponseButton';
import { AiEvaluateProfile } from './AiEvaluateProfile';

// Interface
// ************************************
export interface JobActionsProps {
  job: ApiJobDetailOk & ApiJobUserDetailOk & ApiSchema<'JobItem'>,
  mutateJob?: () => void;
}

/**
 * @description Job detail - Job Actions component.
 * - **job** - ApiJobDetailOk. The job detail.
 */
export const JobActions = (props: JobActionsProps) => {
  // Destructure props
  const { job, mutateJob } = props;

  // Translation
  const { t } = useTranslation('common');

  // Check whether the user is authenticated
  // **************************************
  const { isSafeAuth } = useAuthClient();

  // Router for locale
  const { locale } = useExtendedRouter();

  // Get tracking context data
  // **************************************
  const {
    requestId,
  } = useContext(JobTrackingContext) || {};

  // Job Talent Actions Hook
  // *****************************************
  const {
    applyToJob,
    isApplying,
    hasApplied,
    isFavorite,
    isLoadingFavoritesAction,
    removeFromFavorites,
    addToFavorites,
  } = useJobTalentActions({
    jobId: job.jobId?.toString(),
    jobTitle: job.title,
    company: job.employer?.employerName,
    jobSlug: job.slug,
    likedProp: !!job.liked,
    dislikedProp: !!job.disliked,
    appliedProp: !!job.applied,
    applyCallback: () => { if (mutateJob) { void mutateJob(); } },
    requestId,
    placement: JobPlacement.DETAIL,
    hasOwnApplyUrl: job.hasOwnApplyUrl,
    withEasyApply: true,
    allowAfterApply: !job?.workplaceJob,
  });

  // Share modal
  const { isOpen: isOpenShareModal, toggleModal: toggleShareModal } = useBjModal();

  // Report evaluation modal
  const { isOpen: isReportEvaluationModalOpen, toggleModal: toggleReportEvaluationModal } = useBjModal();

  // Helper Object that stores the classes needed for tracking
  // *****************************************
  const trackingClasses = {
    like: 'tr-job-like',
  };

  const [trackingClassesApply, setTrackingClassesApply] = useState<string>('');

  // Update trackingClassesApply if the user is logged in
  // *****************************************
  useEffect(() => {
    setTrackingClassesApply(clsx('tm-job-apply', !isSafeAuth && 'tr_easy_apply_login'));
  }, [isSafeAuth]);

  // Helper Classes
  // *****************************************
  const buttonBaseClasses = 'px-1 !transition !duration-200 lg:!px-3';
  const buttonLikeClasses = clsx(buttonBaseClasses, trackingClasses.like);
  const buttonIconClasses = 'size-5';
  const buttonSpinnerClasses = 'size-5 mx-0 text-white';

  // Whether the candidate has applied but has unanswered interview questions
  const needToCompleteInterview = !!(job?.appliedAt && job?.unansweredJobInterviewQuestions && job?.unansweredJobInterviewQuestions?.length > 0);

  // Render component
  // *****************************************
  const renderApplicationActions = () => {
    // Return nothing if job.state is missing or falsy
    if (!job?.state) {
      return null;
    }

    if (job?.state === 'archived' || hasApplied) {
      return null;
    }

    return (
      <>
        {/* Apply button */}
        <Button
          type="button"
          size="sm"
          className={clsx('flex-1 !transition !duration-200', trackingClassesApply)}
          color="secondary"
          rounding="2xl"
          isLoading={isApplying}
          onClick={applyToJob}
        >
          {!isApplying && <CheckIcon className="-ml-1 mr-2 size-4" />}
          <span>
            {(() => {
              if (job?.state === 'active') {
                return t('job.apply');
              }
              if (job?.state === 'passive') {
                return t('global.bnt.fast.apply.workplace.job');
              }
              return null;
            })()}
          </span>
        </Button>
      </>
    );
  };

  const actionBar = (
    <div
      className="sticky bottom-1 z-50 mx-4 flex items-center justify-center gap-2 rounded-[24px] border border-input bg-surface px-4 py-5 shadow-lg backdrop-blur-sm lg:bottom-4 lg:mx-8 lg:w-[calc(50%-4rem)] lg:gap-5 lg:rounded-[24px] lg:px-7"
    >

      {/* Share button */}
      {job?.slug && job?.title && (
        <Button
          type="button"
          rounding="2xl"
          color="light"
          size="sm"
          className="!p-3"
          onClick={toggleShareModal}
        >
          <ShareIcon className="size-5" />
        </Button>
      )}

      {/* Save / Favorite button */}
      {job?.state && job?.state !== 'archived' && !hasApplied && (
        <Button
          type="button"
          rounding="2xl"
          size="sm"
          color="light"
          isLoading={isLoadingFavoritesAction}
          className={clsx(buttonLikeClasses, '!p-2.5 lg:!px-3')}
          spinnerClass={buttonSpinnerClasses}
          onClick={isFavorite ? removeFromFavorites : addToFavorites}
        >
          {!isLoadingFavoritesAction && (
            isFavorite
              ? <BookmarkIconSolid className={buttonIconClasses} />
              : <BookmarkIcon className={buttonIconClasses} />
          )}
        </Button>
      )}

      {/* Evaluate CV button - only when no evaluation present */}
      {job && !job.workplaceJob && job.slug && !job.evaluation && (
        <AiEvaluateProfile
          jobSlug={job.slug}
          locale={locale}
          currentEvaluation={job?.evaluation}
          mutateUserDetail={mutateJob}
          renderAsButton
        />
      )}

      {/* Report evaluation button - only when evaluation exists */}
      {job?.evaluation?.id && (
        <Button
          type="button"
          rounding="2xl"
          color="light"
          size="sm"
          className="!p-2.5 !transition !duration-200 lg:!px-7"
          onClick={toggleReportEvaluationModal}
        >
          <FlagIcon className="size-5 lg:hidden" />
          <span className="hidden lg:inline">{t('cv.action.report.evaluation')}</span>
        </Button>
      )}

      {renderApplicationActions()}

      {/* Start Interview button - only if applied and has unanswered interview questions */}
      {needToCompleteInterview && (
        <div className="my-1 flex flex-col items-start gap-2">
          {needToCompleteInterview && (
            <InterviewResponseButton
              jobSlug={job.slug}
              className="flex flex-1"
            />
          )}
        </div>
      )}

    </div>
  );

  return (
    <>
      {actionBar}

      {/* Report evaluation modal */}
      {job?.evaluation?.id && (
        <AiEvaluationFeedbackModal
          isOpen={isReportEvaluationModalOpen}
          toggleModal={toggleReportEvaluationModal}
          identifier={job.evaluation.id.toString()}
          evaluation={job.evaluation}
        />
      )}

      {/* Share modal */}
      {job?.slug && job?.title && (
        <ShareModal
          isOpen={isOpenShareModal}
          onClose={toggleShareModal}
          ogTitle={job.title}
          ogDescription={job.title}
          pathnameShareUrl={getI18nLinkHref(`/job/${job.slug}`, locale)}
        />
      )}
    </>
  );
};
